"use client";

import { MODERATOR_COMMUNITIES_API } from "@/lib/apis";
import { GLOBAL_FEED_SLUG } from "@/lib/constants";
import { useAuth } from "@/providers/auth-provider";
import { TChannelInfo } from "@/types/channel";
import { TCommunityInfo, TGetModeratorCommunitiesDTO, TModeratorChannel, TModeratorCommunity } from "@/types/community";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface CommunityContextProps {
	isSidebarMinimized: boolean;
	communityInfo?: TCommunityInfo;
	channelInfo?: TChannelInfo;
	setCommunityInfo: (communityInfo: TCommunityInfo) => void;
	setChannelInfo: (channelInfo: TChannelInfo) => void;
	handleMinimizeSidebar: () => void;
	isCommunityModerator: boolean;
	isChannelModerator: boolean;
	moderatorCommunities: TModeratorCommunity[];
	moderatorChannels: TModeratorChannel[];
	isFetchingModeratorData: boolean;
	isPublic: boolean;
}

const CommunityContext = createContext<CommunityContextProps | undefined>(undefined);

export const CommunityProvider = ({
	communityInfo: defaultcommunityInfo,
	channelInfo: defaultchannelInfo,
	children,
}: {
	communityInfo?: TCommunityInfo;
	channelInfo?: TChannelInfo;
	children: ReactNode;
}) => {
	const { isLoggedIn } = useAuth();
	const [channelInfo, setChannelInfo] = useState<TChannelInfo>();
	const [communityInfo, setCommunityInfo] = useState<TCommunityInfo>();
	const [isSidebarMinimized, setIsSidebarMinimized] = useState<boolean>(false);

	const [moderatorCommunities, setModeratorCommunities] = useState<TModeratorCommunity[]>([]);
	const [isFetchingModeratorData, setIsFetchingModeratorData] = useState<boolean>(false);

	const fetchModeratorCommunities = useCallback(async () => {
		if (!isLoggedIn) {
			setModeratorCommunities([]);
			return;
		}

		try {
			setIsFetchingModeratorData(true);
			const response = await fetch(MODERATOR_COMMUNITIES_API);
			const { data } = (await response.json()) as TGetModeratorCommunitiesDTO | { data: undefined };

			const formattedCommunities = data?.map((community) => ({
				...community,
				channels: community.channels.map((channel) => ({
					id: channel.id,
					type: null,
					name: channel.name,
					slug: channel.slug,
				})),
			})) ?? [];

			setModeratorCommunities(formattedCommunities);
		} catch (error) {
			console.log("Error occurred while fetching moderator communities");
			setModeratorCommunities([]);
		} finally {
			setIsFetchingModeratorData(false);
		}
	}, [isLoggedIn]);

	const moderatorChannels = useMemo(() => {
		const currentCommunity = moderatorCommunities.find((community) => community.slug === communityInfo?.slug);
		return currentCommunity?.channels || [];
	}, [moderatorCommunities, communityInfo?.slug]);

	const isCommunityModerator = useMemo(() => {
		return !!moderatorCommunities.find((community) => community.slug === communityInfo?.slug);
	}, [moderatorCommunities, communityInfo?.slug]);

	const isChannelModerator = useMemo(() => {
		return !!moderatorChannels.find((channel) => channel.slug === channelInfo?.slug);
	}, [moderatorChannels, channelInfo?.slug]);

	const isPublic = useMemo(() => {
		return communityInfo?.slug === GLOBAL_FEED_SLUG;
	}, [communityInfo?.slug]);

	useEffect(() => {
		if (defaultcommunityInfo) {
			setCommunityInfo(defaultcommunityInfo);
		} else {
			setCommunityInfo(undefined);
		}
	}, [defaultcommunityInfo]);

	useEffect(() => {
		setChannelInfo(defaultchannelInfo);
	}, [defaultchannelInfo]);

	useEffect(() => {
		fetchModeratorCommunities();
	}, [fetchModeratorCommunities]);

	const handleMinimizeSidebar = () => {
		setIsSidebarMinimized((prevState) => !prevState);
	};

	return (
		<CommunityContext.Provider
			value={{
				channelInfo,
				communityInfo,
				setCommunityInfo,
				setChannelInfo,
				isSidebarMinimized,
				handleMinimizeSidebar,
				isCommunityModerator,
				isChannelModerator,
				moderatorCommunities,
				moderatorChannels,
				isFetchingModeratorData,
				isPublic,
			}}
		>
			{children}
		</CommunityContext.Provider>
	);
};

export const useCommunityContext = () => {
	const context = useContext(CommunityContext);

	if (!context) {
		throw new Error("useCommunityContext must be used within a CommunityProvider");
	}

	return context;
};
